import { Button } from "@excalidraw-ms/shared-ui";
import { useEffect, useId, useRef, useState } from "react";
import type { BackendAccessPhase, BackendCapabilityState } from "../auth/msalClient";
import BrandIcon from "./BrandIcon";
import { ACCESS_REQUEST_URL } from "../env";

interface FullAccessDialogProps {
  state: BackendCapabilityState;
  phase: BackendAccessPhase;
  onRetry: () => void;
  onClose: () => void;
}

export default function FullAccessDialog({
  state,
  phase,
  onRetry,
  onClose,
}: FullAccessDialogProps) {
  const panel = useRef<HTMLElement>(null);
  const headingId = useId();
  const [attempted, setAttempted] = useState(false);
  const [membershipCheck, setMembershipCheck] = useState(state === "local-only");
  const busy = state === "checking" || state === "full";
  const isError = state === "temporarily-unavailable";
  const needsMembership = state === "local-only" || (state === "checking" && membershipCheck);
  const message =
    state === "checking"
      ? phase === "preparing-cloud"
        ? "Preparing cloud features…"
        : "Checking Excalidraw-MS access…"
      : isError
        ? "Cloud features are temporarily unavailable. Please try again."
        : attempted
          ? "Your access is not ready yet. If you've joined, your membership may still be updating. Wait a moment, then try again."
          : "Return here and select I've joined to check your access.";

  useEffect(() => {
    if (state === "full") onClose();
  }, [state, onClose]);

  useEffect(() => {
    const previous = document.activeElement;
    panel.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
      if (event.key === "Tab") {
        const targets = panel.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)");
        const first = targets?.[0];
        const last = targets?.[targets.length - 1];
        if (!first || !last) {
          event.preventDefault();
        } else if (
          event.shiftKey &&
          (document.activeElement === first || document.activeElement === panel.current)
        ) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          (document.activeElement === last || document.activeElement === panel.current)
        ) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (previous instanceof HTMLElement && previous !== document.body && previous.isConnected) {
        previous.focus();
      } else {
        // Native menu items unmount when selected; return to the surviving entry point.
        const trigger =
          document.querySelector<HTMLElement>(".main-menu-trigger") ??
          document.querySelector<HTMLElement>("[data-full-access-trigger]");
        trigger?.focus();
      }
    };
  }, [onClose]);

  if (state === "full") return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10001,
        background: "var(--exms-overlay-bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <section
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        style={{
          boxSizing: "border-box",
          width: "calc(100% - 32px)",
          maxWidth: 480,
          maxHeight: "calc(100dvh - 32px)",
          overflowY: "auto",
          padding: "clamp(24px, 5vw, 32px)",
          textAlign: "left",
          color: "var(--text-primary-color)",
          background: "var(--island-bg-color)",
          border: "1px solid var(--dialog-border-color)",
          borderRadius: 12,
          boxShadow: "var(--exms-shadow-card)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <BrandIcon size={24} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Excalidraw-MS</span>
        </div>
        <h1
          id={headingId}
          style={{
            fontSize: 26,
            fontWeight: 600,
            letterSpacing: "-0.5px",
            margin: "0 0 12px",
            lineHeight: 1.25,
          }}
        >
          Get full access
        </h1>
        {needsMembership && (
          <>
            <p
              style={{
                color: "var(--color-text-secondary)",
                fontSize: 14,
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              Join the Excalidraw-MS group to enable cloud saving, sharing, and live collaboration.
            </p>
            <p
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                lineHeight: 1.6,
                margin: "20px 0 24px",
                color: "var(--color-text-secondary)",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                aria-hidden="true"
                style={{ flexShrink: 0 }}
              >
                <circle cx="12" cy="12" r="9" />
                <path
                  d={ACCESS_REQUEST_URL ? "m8 12 3 3 5-6" : "M12 11v6m0-10v1"}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {ACCESS_REQUEST_URL
                ? "Membership is approved automatically."
                : "Ask your admin to add you to the Excalidraw-MS access group."}
            </p>
          </>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {needsMembership && ACCESS_REQUEST_URL && (
            <Button
              variant="primary"
              style={{
                flex: "1 1 150px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
              onClick={() => window.open(ACCESS_REQUEST_URL, "_blank", "noopener,noreferrer")}
            >
              Join group
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path
                  d="M14 3h7v7m0-7L10 14M10 5H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Button>
          )}
          {onRetry && (
            <Button
              variant="primary"
              style={{ flex: "1 1 150px" }}
              disabled={busy}
              onClick={() => {
                setAttempted(true);
                setMembershipCheck(state === "local-only");
                onRetry();
              }}
            >
              {needsMembership ? "I've joined" : "Try again"}
            </Button>
          )}
        </div>
        {needsMembership && (
          <p
            style={{
              fontSize: 12,
              color: "var(--color-text-secondary)",
              lineHeight: 1.6,
              margin: "12px 0 0",
            }}
          >
            {ACCESS_REQUEST_URL
              ? "Join opens MyAccount in a new tab."
              : "Once access is granted, select I've joined."}
          </p>
        )}
        <p
          role={isError ? "alert" : "status"}
          aria-atomic="true"
          style={{
            minHeight: needsMembership ? 40 : undefined,
            margin: "24px 0 0",
            padding: "16px 0 0",
            borderTop: "1px solid var(--dialog-border-color)",
            fontSize: 13,
            lineHeight: 1.6,
            color: isError ? "var(--color-danger)" : "var(--color-text-secondary)",
          }}
        >
          {message}
        </p>
      </section>
    </div>
  );
}
